import { useEffect } from 'react'
import { Star, ChevronRight, CalendarDays, CarTaxiFront, ShoppingCart, Bike, UserRound } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import TabBar from '../components/TabBar'
import { useStore, filterVisibleStores } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { useMyReservations } from '../hooks/useMyReservations'
import { useMyOrders } from '../hooks/useMyOrders'
import { isSelfOrderEnabled, isDeliveryEnabled, isRideEnabled } from '../lib/features'
import { getGuestName } from '../lib/guestProfile'
import EcEntryCard from '../components/EcEntryCard'
import HomeTicketsCard from '../components/HomeTicketsCard'
import StampRallyEntryCard from '../components/StampRallyEntryCard'
import StoreRecoBadge from '../components/StoreRecoBadge'
import AnnouncementPopup from '../components/AnnouncementPopup'
import { useGeolocation } from '../hooks/useGeolocation'
import LocationAskCard from '../components/LocationAskCard'
import { useStoreReco } from '../hooks/useStoreReco'

function getGreetingKey(): string {
  const hour = new Date().getHours()
  if (hour >= 0 && hour <= 4) return 'home.hello'
  if (hour >= 5 && hour <= 11) return 'home.goodMorning'
  if (hour >= 12 && hour <= 17) return 'home.goodAfternoon'
  return 'home.goodEvening'
}

export default function Home() {
  const navigate = useNavigate()
  const { user, isLoggedIn, stores: allStores, setCurrentStore, loadStores, budgetEnabled, budgetAmount, getBudgetSpending } = useStore()
  const stores = filterVisibleStores(allStores)
  // #1384 お知らせの位置判定に使う。このフックは **requestLocation を呼ばない限り
  // 許可を求めない**（キャッシュ or ?lat=&lng= のデモ値だけを読む）ので、
  // 案内のために位置情報の許可を押し付けることにはならない。
  const { coords } = useGeolocation()
  const { active: activeReservations } = useMyReservations()
  const pendingReservations = activeReservations.filter(r => r.status === 'pending').length
  const approvedReservations = activeReservations.filter(r => r.status === 'approved').length
  const { orders: allMyOrders, tableOrders } = useMyOrders()
  // Home surfaces only orders that still need the customer's attention
  // — delivered orders shouldn't clutter Home even within the 30-minute
  // grace period. Dine-in: unpaid bill. Delivery: pipeline in flight or
  // an open missing-report dispute. Post-delivery navigation back to
  // /my-orders (for late missing-report) is via /history, which carries
  // a 30-min "注文詳細を見る" link for just-delivered orders.
  const openOrders = allMyOrders.filter((o) => {
    if (o.status === 'cancelled') return false
    if (o.orderType === 'delivery') {
      const inPipeline =
        o.status === 'received' || o.status === 'preparing' || o.status === 'out_for_delivery'
      return inPipeline || !!o.missingReported
    }
    // #1217 カウンター受け取りは先払いなので「未払い = 進行中」ではない。
    // 決済前 (pending_payment) はまだ注文になっておらず、受け渡し済み
    // (picked_up) は完了。準備中だけを進行中として数える。
    if (o.orderType === 'takeout') {
      return o.status === 'received' || o.status === 'preparing' || o.status === 'ready'
    }
    return !o.paid
  })
  const activeOrderStores = (() => {
    const map = new Map<string, { storeId: string; storeName: string; count: number; unpaid: number; isTable?: boolean }>()
    for (const o of openOrders) {
      const storeName = allStores.find((s) => s.id === o.storeId)?.name || o.storeId
      // REQ-28 ではなく REQ-16 (#1579): **支払い済みの額を未払いに足さない**。
      //
      // 上の openOrders は「先払いチャネル (カウンター受け取り / 出前) は
      // 未払い = 進行中ではない」と正しく分けているのに、ここが分岐を無視して
      // 全部を unpaid に足していた。その結果、決済直後の客に
      // **「未払い ¥580」と出る** (サーバは paid=True で正しい)。
      const unpaid = o.paid ? 0 : o.totalAmount
      const existing = map.get(o.storeId)
      if (existing) {
        existing.count += 1
        existing.unpaid += unpaid
      } else {
        map.set(o.storeId, { storeId: o.storeId, storeName, count: 1, unpaid })
      }
    }
    // #1535 卓に着席中はその店の額を**卓全体** (自分 + 友達 + 店員入力) に置き換える。
    // 店内の伝票は卓単位 — 自分の分だけを出すと卓の合計と誤読され、会計時に金額が
    // 跳ねて揉める。会計対象の定義はサーバの table_chargeable_orders に揃える。
    const seated = tableOrders[0]
    if (seated) {
      const entry = map.get(seated.storeId)
      if (entry) {
        const chargeable = (o: { paid?: boolean; status: string; orderType?: string }) =>
          !o.paid && o.status !== 'cancelled' && o.status !== 'pending_approval'
          && o.orderType !== 'delivery'
        const slip = [
          ...openOrders.filter((o) =>
            o.storeId === seated.storeId && o.tableNumber === seated.tableNumber && chargeable(o)),
          ...tableOrders.filter(chargeable),
        ]
        entry.unpaid = slip.reduce((n, o) => n + o.totalAmount, 0)
        entry.count = slip.length
        entry.isTable = true
      }
    }
    return Array.from(map.values())
  })()
  // #1579 カードに出す未払い合計。0 なら「支払済み」表示に切り替える。
  const unpaidTotal = activeOrderStores.reduce((n, g) => n + g.unpaid, 0)
  const { t } = useI18n()
  const greeting = t(getGreetingKey())
  // #1246: ゲストは localStorage の表示名 (プロフィールで設定可)、無ければ
  // ローカライズされた「ゲスト」。ログイン済みは従来どおりアカウント名。
  const displayName = isLoggedIn ? (user?.name ?? 'Alex') : getGuestName() || t('guest.label')

  // Load stores from API on mount
  useEffect(() => {
    loadStores()
  }, [loadStores])

  // #8: おすすめスコア (実利用データ由来) があれば発見枠をスコア降順に並べ替える。
  // スコアの無い店は stable sort で元の順のまま後ろに残る (隠さない)。
  const { scores: recoScores, recoAvailable } = useStoreReco()
  const discoveryStores = recoAvailable
    ? [...stores].sort(
        (a, b) => (recoScores[b.id]?.score ?? 0) - (recoScores[a.id]?.score ?? 0),
      )
    : stores

  // Travel budget
  const budgetSpending = budgetEnabled ? getBudgetSpending() : 0
  const budgetPercent = budgetEnabled && budgetAmount > 0 ? Math.min((budgetSpending / budgetAmount) * 100, 100) : 0
  const budgetRemaining = budgetEnabled ? Math.max(budgetAmount - budgetSpending, 0) : 0
  const budgetBarColor = budgetPercent >= 100 ? '#E63946' : budgetPercent >= 80 ? '#C9A84C' : '#4CAF50'

  return (
    <div className="relative min-h-screen bg-warm-white safe-top safe-bottom">
      <div className="pb-[83px]">
        {/* Greeting (#1246: ゲストは「こんにちは、ゲスト」— 表示名を設定していればその名前) */}
        <div className="animate-fade-in px-5 pt-6 pb-4">
          <p className="font-body text-[15px] text-text-secondary">
            {greeting}, {displayName} 👋
          </p>
        </div>

        {/* #1246: ゲスト向け登録導線カード。ウォレット残高カードの位置に置き、
          * 「使いたくなったら登録」への入口を常設する (強制モーダルにはしない)。 */}
        {!isLoggedIn && (
          <button
            onClick={() => navigate('/login')}
            className="tap-card animate-fade-in mx-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl bg-navy p-4 text-left shadow-card"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15">
              <UserRound size={20} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-semibold text-white">
                {t('home.guestCta.title')}
              </p>
              <p className="mt-0.5 font-body text-xs leading-relaxed text-white/80">
                {t('home.guestCta.subtitle')}
              </p>
            </div>
            <ChevronRight size={18} className="shrink-0 text-white/70" />
          </button>
        )}

        {/* #1188 Phase 2: 残高カード / 次回失効サマリ / チャージ・履歴ボタンは
           独立した /wallet タブに移動。Home は discovery (店舗・出前・スタンプ)
           に集約し、金融機能とはページを分ける。 */}

        {/* Budget Bar */}
        {budgetEnabled && (
          <div
            onClick={() => navigate('/travel-budget')}
            className="tap-effect mx-4 mt-3 cursor-pointer rounded-xl bg-white px-4 py-3 shadow-card"
          >
            <div className="flex items-center justify-between">
              <span className="font-body text-xs text-text-secondary">{t('budget.remaining')}</span>
              <span className="font-mono text-xs font-medium text-text-primary">
                &yen;{budgetRemaining.toLocaleString()} / &yen;{budgetAmount.toLocaleString()}
              </span>
            </div>
            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${budgetPercent}%`, backgroundColor: budgetBarColor }}
              />
            </div>
          </div>
        )}

        {/* #879 / #880 EC <-> YOKOSO PAY SSO: お土産ショップ入口。#1277 FB: EC は
           他の旅中導線と関連が薄い独立した入口なので最上部に置く (中間に挟むと違和感)。
           VITE_EC_URL 未設定なら null。旅前プランは「旅程」タブに集約したためホームの
           プラン入口カードは廃止 (重複解消)。 */}
        <EcEntryCard />

        {/* #1285: 保有中の体験チケット (利用可能なチケットがある時だけ表示) */}
        <HomeTicketsCard />

        {/* Active orders card — shown only when user has active self-orders */}
        {isSelfOrderEnabled() && activeOrderStores.length > 0 && (
          <button
            onClick={() => navigate('/my-orders')}
            className="tap-card animate-fade-in mt-4 mx-4 w-[calc(100%-2rem)] flex items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-card border-2 border-yokoso-red/20"
            style={{ animationDelay: '0.2s', animationFillMode: 'both' }}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yokoso-red/10">
              <ShoppingCart size={20} className="text-yokoso-red" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-text-primary text-sm truncate">
                {activeOrderStores.length === 1
                  ? t('home.orderCardSingle', { store: activeOrderStores[0].storeName, count: activeOrderStores[0].count })
                  : t('home.orderCardMulti', { count: openOrders.length })}
              </p>
              <p className="font-body text-xs text-text-secondary mt-0.5">
                {/* #1535 着席中はテーブル全員分のラベルで卓の合計を出す。
                 *  #1579 先払いだけで未払いが無い時は金額を出さない — 「未払い ¥0」は
                 *  払い忘れと読めるし、先払いで見せたいのは進行状況の方。
                 *
                 *  文言は「受け取り待ち」。**段階を書かない**のが要点で、先払いは
                 *  受付 / 調理中 / 受け取り可能 の 3 状態を通るため「準備中」と書くと
                 *  受け取り可能なのに準備中と出る (demo で発覚)。 */}
                {unpaidTotal > 0
                  ? t(activeOrderStores.some((g) => g.isTable)
                    ? 'home.orderCardUnpaidTable'
                    : 'home.orderCardUnpaid', { total: unpaidTotal.toLocaleString() })
                  : t('home.orderCardAwaitingPickup')}
              </p>
            </div>
            <ChevronRight size={18} className="text-text-tertiary shrink-0" />
          </button>
        )}

        {/* Delivery entry card (#284) — dedicated flow so customers don't
         * accidentally start a delivery order from the regular store search. */}
        {isDeliveryEnabled() && (
          <button
            onClick={() => navigate('/delivery')}
            className="tap-card animate-fade-in mt-4 mx-4 w-[calc(100%-2rem)] flex items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-card border-2 border-matcha/20"
            style={{ animationDelay: '0.22s', animationFillMode: 'both' }}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-matcha/10">
              <Bike size={20} className="text-matcha" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-text-primary text-sm">
                {t('delivery.entryTitle')}
              </p>
              <p className="font-body text-xs text-text-secondary mt-0.5">
                {t('delivery.entrySubtitle')}
              </p>
            </div>
            <ChevronRight size={18} className="text-text-tertiary shrink-0" />
          </button>
        )}

        {/* #1619 タクシー配車ハブ入口 — Uber / GO への外部リンクと、
          * 協業後のアプリ内配車 (YOKOSOライド) の準備中プレビュー。#2549 本番は出さない */}
        {isRideEnabled() && (
          <button
            onClick={() => navigate('/ride')}
            className="tap-card animate-fade-in mt-4 mx-4 w-[calc(100%-2rem)] flex items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-card"
            style={{ animationDelay: '0.24s', animationFillMode: 'both' }}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy/10">
              <CarTaxiFront size={20} className="text-navy" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-text-primary text-sm">
                {t('home.rideCardTitle')}
              </p>
              <p className="font-body text-xs text-text-secondary mt-0.5">
                {t('home.rideCardSubtitle')}
              </p>
            </div>
            <ChevronRight size={18} className="text-text-tertiary shrink-0" />
          </button>
        )}

        {/* Active reservations card — shown only when user has active reservations */}
        {activeReservations.length > 0 && (
          <button
            onClick={() => navigate('/my-reservations')}
            className="tap-card animate-fade-in mt-4 mx-4 w-[calc(100%-2rem)] flex items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-card"
            style={{ animationDelay: '0.25s', animationFillMode: 'both' }}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy/10">
              <CalendarDays size={20} className="text-navy" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-text-primary text-sm">
                {t('home.reservationCardTitle', { count: activeReservations.length })}
              </p>
              <p className="font-body text-xs text-text-secondary mt-0.5">
                {approvedReservations > 0 && t('home.reservationCardApproved', { count: approvedReservations })}
                {approvedReservations > 0 && pendingReservations > 0 && ' ・ '}
                {pendingReservations > 0 && t('home.reservationCardPending', { count: pendingReservations })}
              </p>
            </div>
            <ChevronRight size={18} className="text-text-tertiary shrink-0" />
          </button>
        )}

        {/* #1126 スタンプラリー開催中カード（旅中イベント・無ければ何も描画しない）。
          * #1413 座標を渡して**現在地の地域**のイベント名を出す。取れていなければ
          * カード側がイベント名を伏せる。**ここでも位置の許可は求めない** —
          * useGeolocation は許可済みのキャッシュ (or ?lat=&lng=) を読むだけ。 */}
        {/* 既にオンボーディングを終えている人に、初回 1 回だけ位置情報の使い道を説明して聞く。
            ここが決まらないと、下のイベントの入口とお知らせが「現在地が分からないまま」の中身になる */}
        <LocationAskCard />

        <StampRallyEntryCard lat={coords?.lat} lng={coords?.lng} />

        {/* Nearby Stores */}
        <div
          className="animate-fade-in mt-6"
          style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
        >
          {/* Section Header */}
          <div className="flex items-center justify-between px-5">
            <h2 className="font-display text-[17px] font-bold text-text-primary">
              {/* #8: おすすめが効いている時は枠の名前も「おすすめ」にする (誤誘導しない) */}
              {openOrders.length > 0
                ? t('home.currentMeal')
                : recoAvailable
                  ? t('home.recommendedStores')
                  : t('home.nearbyStores')}
            </h2>
            {openOrders.length === 0 && (
              <button
                onClick={() => navigate('/stores')}
                className="tap-effect flex items-center gap-0.5 font-body text-[13px] font-medium text-yokoso-red"
              >
                {t('home.viewAll')}
                <ChevronRight size={16} />
              </button>
            )}
          </div>

          {/* During an active session, only the active store is shown so the
           * customer focuses on adding to their current meal. After payment,
           * normal nearby-store browsing returns. */}
          <div className="scrollbar-hide mt-3 flex gap-3 overflow-x-auto px-5">
            {(openOrders.length > 0
              ? stores.filter((s) => activeOrderStores.some((g) => g.storeId === s.id))
              : discoveryStores
            ).map((store) => (
              <button
                key={store.id}
                onClick={() => {
                  setCurrentStore(store)
                  navigate(`/stores/${store.id}`)
                }}
                className="tap-card flex w-[140px] shrink-0 flex-col overflow-hidden rounded-xl bg-white shadow-card"
              >
                {/* Store image */}
                <div className="h-[100px] w-full bg-gradient-to-br from-gray-100 to-gray-200">
                  <img
                    src={store.imageUrl}
                    alt={store.nameEn}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex flex-col gap-1 p-2.5">
                  <p className="truncate text-left font-body text-[13px] font-medium text-text-primary">
                    {store.nameEn}
                  </p>
                  {/* #8 実データ推薦の理由バッジ (該当店のみ) */}
                  <StoreRecoBadge
                    reason={recoScores[store.id]?.reason}
                    className="inline-block max-w-full self-start truncate"
                  />
                  <div className="flex items-center gap-1">
                    {/* レビューが無い店は「★0」でなく「新着」表示（違和感解消） */}
                    {store.rating > 0 ? (
                      <>
                        <Star size={12} className="fill-gold text-gold" />
                        <span className="font-body text-[12px] text-text-secondary">
                          {store.rating}
                        </span>
                      </>
                    ) : (
                      <span className="font-body text-[12px] text-text-tertiary">
                        {t('stores.newStore')}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* #1384 旅先で役に立つお知らせ。位置が取れていれば現在地に合うものを、
        * 取れていなければ場所を問わない案内だけを出す。
        * **位置の許可はここでは求めない** — 他機能で既に許可済みの座標があれば使う、
        * という受け身の姿勢にする (案内のために許可を求めると押し付けになる)。 */}
      <AnnouncementPopup lat={coords?.lat} lng={coords?.lng} />

      {/* Tab Bar */}
      <TabBar />
    </div>
  )
}
